import React from "react";
import { Link } from "react-router-dom";
import { useToast } from "./Toast";
import { copyToClipboard } from "../utils/clipboard";
import "./ui.css";

/**
 * Sdílené karty: ActionList (navigační seznamy), InfoCard (stavové karty
 * s ikonou – dashboard střelce, členství), DetailList + DetailRow (řádky
 * popisek / hodnota – profil, platební údaje) a StatTile (KPI dlaždice –
 * Přehled systému, krok 9).
 */

export type InfoCardTone =
  | "brand"
  | "success"
  | "danger"
  | "warning"
  | "info"
  | "dark";

export interface InfoCardAction {
  label: string;
  onClick?: () => void;
  /** Odkaz – vykreslí se tlačítko navigující přes onNavigate rodiče. */
  to?: string;
  variant?: "light" | "primary";
}

export interface InfoCardProps {
  /** Ikona bootstrap-icons, např. "bi-credit-card". */
  icon: string;
  tone: InfoCardTone;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Tlačítko vpravo (pod 400 px na celou šířku pod textem). */
  action?: InfoCardAction;
  onNavigate?: (to: string) => void;
  /** Badge vedle titulku. */
  badge?: React.ReactNode;
  /** Menší padding – do hustších seznamů. */
  compact?: boolean;
  /** Celá karta klikací (role button, fokusovatelná). */
  onClick?: () => void;
  className?: string;
  children?: React.ReactNode;
}

export const InfoCard: React.FC<InfoCardProps> = ({
  icon,
  tone,
  title,
  subtitle,
  action,
  onNavigate,
  badge,
  compact = false,
  onClick,
  className,
  children,
}) => {
  const interactive = typeof onClick === "function";
  const handleAction = () => {
    if (!action) return;
    if (action.onClick) action.onClick();
    else if (action.to && onNavigate) onNavigate(action.to);
  };

  return (
    <div
      className={[
        "lks-infocard",
        `lks-infocard--${tone}`,
        compact ? "lks-infocard--compact" : "",
        interactive ? "lks-infocard--clickable" : "",
        className || "",
      ]
        .filter(Boolean)
        .join(" ")}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        interactive
          ? (e) => {
              // Interaktivní potomci (akční tlačítko) si klávesy řeší sami.
              if (e.target !== e.currentTarget) return;
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick!();
              }
            }
          : undefined
      }
    >
      <span className="lks-infocard__icon" aria-hidden="true">
        <i className={`bi ${icon}`} />
      </span>
      <span className="lks-infocard__body">
        <span className="lks-infocard__title">
          {title}
          {badge}
        </span>
        {subtitle ? (
          <span className="lks-infocard__subtitle">{subtitle}</span>
        ) : null}
        {children}
      </span>
      {action && (action.onClick || (action.to && onNavigate)) ? (
        <button
          type="button"
          className={`btn ${
            action.variant === "primary" ? "btn-primary" : "btn-light"
          } lks-infocard__action`}
          onClick={(e) => {
            e.stopPropagation();
            handleAction();
          }}
        >
          {action.label}
        </button>
      ) : null}
    </div>
  );
};

/**
 * ActionList – seznam navigačních/akčních položek (rozcestníky, menu).
 * Položka má ikonu, popisek, volitelně popis a šipku; minimální výška 44 px.
 */

export interface ActionListItem {
  icon: string;
  label: string;
  /** Doplňkový popis pod popiskem. */
  description?: string;
  to?: string;
  onClick?: () => void;
  tone?: "default" | "danger" | "primary";
  /** Šipka vpravo (default true; false např. pro Odhlásit). */
  chevron?: boolean;
  /** Badge vpravo před šipkou (počet, stav). */
  badge?: React.ReactNode;
}

export interface ActionListProps {
  items: ActionListItem[];
  /** Naviguje se přes react-router – předává AppShell stránka. */
  onNavigate?: (to: string) => void;
  className?: string;
}

export const ActionList: React.FC<ActionListProps> = ({
  items,
  onNavigate,
  className,
}) => (
  <div className={className ? `list-group ${className}` : "list-group"}>
    {items.map((item) => {
      const handle = () => {
        if (item.onClick) item.onClick();
        else if (item.to && onNavigate) onNavigate(item.to);
      };
      return (
        <button
          key={item.label}
          type="button"
          className={`list-group-item list-group-item-action lks-actionlist__item${
            item.tone === "danger" ? " lks-actionlist__item--danger" : ""
          }${item.tone === "primary" ? " lks-actionlist__item--primary" : ""}`}
          onClick={handle}
        >
          <i
            className={`bi ${item.icon} lks-actionlist__icon`}
            aria-hidden="true"
          />
          <span className="lks-actionlist__text">
            <span className="lks-actionlist__label">{item.label}</span>
            {item.description ? (
              <span className="lks-actionlist__desc">{item.description}</span>
            ) : null}
          </span>
          {item.badge}
          {item.chevron !== false && (
            <i
              className="bi bi-chevron-right lks-actionlist__chevron"
              aria-hidden="true"
            />
          )}
        </button>
      );
    })}
  </div>
);

/**
 * DetailList + DetailRow – řádky „popisek: hodnota" (údaje profilu,
 * platební údaje). DetailList je sémantický <dl>, DetailRow jeho položka
 * (<div> s <dt> a <dd>) – DetailRow proto patří vždy do DetailList.
 * Popisek vlevo, hodnota vpravo; dlouhá hodnota (e-mail) se zalomí.
 */

export interface DetailListProps {
  className?: string;
  children: React.ReactNode;
}

export const DetailList: React.FC<DetailListProps> = ({
  className,
  children,
}) => (
  <dl className={className ? `lks-detaillist ${className}` : "lks-detaillist"}>
    {children}
  </dl>
);

export interface DetailRowProps {
  label: React.ReactNode;
  value: React.ReactNode;
  /**
   * Tlačítko „Kopírovat" vedle hodnoty (číslo účtu, VS, částka). Kopíruje
   * copyText, jinak value, pokud je to text nebo číslo; po zkopírování
   * toast „Zkopírováno".
   */
  copyable?: boolean;
  /** Text ke zkopírování, když se liší od zobrazené hodnoty (např. IBAN bez mezer). */
  copyText?: string;
  /** Dlouhá hodnota se zalamuje (výchozí). false = jeden řádek s výpustkou. */
  wrap?: boolean;
  className?: string;
}

export const DetailRow: React.FC<DetailRowProps> = ({
  label,
  value,
  copyable = false,
  copyText,
  wrap = true,
  className,
}) => {
  const { showToast } = useToast();

  const textToCopy =
    copyText !== undefined
      ? copyText
      : typeof value === "string" || typeof value === "number"
      ? String(value)
      : "";
  const canCopy = copyable && textToCopy !== "";
  const copyLabel =
    typeof label === "string" ? `Kopírovat: ${label}` : "Kopírovat";

  const handleCopy = async () => {
    const ok = await copyToClipboard(textToCopy);
    if (ok) showToast("Zkopírováno", "success");
    else showToast("Kopírování se nepodařilo – označte text ručně.", "warning");
  };

  return (
    <div
      className={[
        "lks-detailrow",
        wrap ? "lks-detailrow--wrap" : "lks-detailrow--nowrap",
        className || "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <dt className="lks-detailrow__label">{label}</dt>
      <dd className="lks-detailrow__value">
        <span className="lks-detailrow__text">{value}</span>
        {canCopy ? (
          <button
            type="button"
            className="lks-detailrow__copy"
            aria-label={copyLabel}
            title={copyLabel}
            onClick={handleCopy}
          >
            <i className="bi bi-copy" aria-hidden="true" />
          </button>
        ) : null}
      </dd>
    </div>
  );
};

/**
 * StatTile – KPI dlaždice přehledů (Přehled systému): popisek, velká hodnota
 * a volitelný doplňující řádek, vlevo kruhová ikona v barvě tónu (vzor
 * InfoCard). Jen Bootstrap třídy (card, h-100, fs-*, text-body-secondary,
 * bg-*-subtle, text-*-emphasis – barvy přemapovává tokens.css), vlastní CSS
 * v ui.css nemá. S `to` je celá dlaždice odkaz (react-router Link) se šipkou.
 * Dlaždice vyplní výšku buňky mřížky (h-100) – patří do .row > .col.
 */

export type StatTileTone =
  | "neutral"
  | "brand"
  | "success"
  | "danger"
  | "warning"
  | "info";

export interface StatTileProps {
  label: string;
  value: string | number;
  /** Doplňující řádek pod hodnotou (rozpad, období). */
  hint?: React.ReactNode;
  /** Barva ikony a hodnoty; danger a warning navíc zvýrazní okraj. Výchozí neutral. */
  tone?: StatTileTone;
  /** Ikona z bootstrap-icons, např. "bi-people-fill". Pod 576 px se skrývá (úzká dlaždice). */
  icon?: string;
  /** Cíl odkazu – celá dlaždice je pak klikací odkaz. */
  to?: string;
  className?: string;
}

/** Tón → barva Bootstrapu (brand = primary, tokens.css ji mapuje na značku). */
const STAT_TILE_COLORS: Record<Exclude<StatTileTone, "neutral">, string> = {
  brand: "primary",
  success: "success",
  danger: "danger",
  warning: "warning",
  info: "info",
};

export const StatTile: React.FC<StatTileProps> = ({
  label,
  value,
  hint,
  tone = "neutral",
  icon,
  to,
  className,
}) => {
  const color = tone === "neutral" ? null : STAT_TILE_COLORS[tone];
  const highlight = tone === "danger" || tone === "warning";
  const cardClass = [
    "card h-100",
    highlight ? `border-${color}-subtle` : "",
    to ? "text-decoration-none text-reset" : "",
    className || "",
  ]
    .filter(Boolean)
    .join(" ");

  const body = (
    <div className="card-body d-flex align-items-center p-3">
      {icon ? (
        <span
          className={`d-none d-sm-flex align-items-center justify-content-center rounded-circle flex-shrink-0 me-3 ${
            color
              ? `bg-${color}-subtle text-${color}-emphasis`
              : "bg-body-tertiary text-body-secondary"
          }`}
          style={{ width: "2.5rem", height: "2.5rem", fontSize: "1.15rem" }}
          aria-hidden="true"
        >
          <i className={`bi ${icon}`} />
        </span>
      ) : null}
      <span className="d-block flex-grow-1" style={{ minWidth: 0 }}>
        <span className="d-block small text-body-secondary">{label}</span>
        <span
          className={`d-block fs-4 fw-bold lh-sm text-break${
            color ? ` text-${color}-emphasis` : ""
          }`}
        >
          {value}
        </span>
        {hint ? (
          <span className="d-block small text-body-secondary">{hint}</span>
        ) : null}
      </span>
      {to ? (
        <i
          className="bi bi-chevron-right flex-shrink-0 ms-2 text-body-secondary"
          aria-hidden="true"
        />
      ) : null}
    </div>
  );

  if (to) {
    return (
      <Link to={to} className={cardClass}>
        {body}
      </Link>
    );
  }
  return <div className={cardClass}>{body}</div>;
};

export default ActionList;
